import * as stylex from '@stylexjs/stylex';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { TooltipTrigger } from 'react-aria-components';

import {
  Button,
  CheckmarkStrokeIcon,
  CopyStrokeIcon,
  DIALOG_SLOTS,
  DialogBar,
  Form,
  Heading,
  Hint,
  Icon,
  IconButton,
  Input,
  Label,
  Snackbar,
  Text,
  TextField,
  Tooltip,
  useCopyToClipboard,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { organizationUserApiKeyMutations } from '@/resources/organization-user-api-key/organization-user-api-key.api';

interface CreateApiKeyDialogContentProps {
  onClose: () => void;
  onCreated?: (apiKey: string) => void;
}

export function CreateApiKeyDialogContent({ onClose, onCreated }: CreateApiKeyDialogContentProps) {
  const [ , copyToClipboard ] = useCopyToClipboard();
  const [ copied, setCopied ] = useState<('apiKey' | 'secretKey')[]>([]);

  const createOrganizationUserApiKeyMutation = useMutation(organizationUserApiKeyMutations.create);
  const form = useForm({
    defaultValues: { name: '' },
    onSubmit: async ({ value }) => {
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

      createOrganizationUserApiKeyMutation.mutate({
        name: value.name,
        expiresAt: oneYearFromNow.toISOString(),
      }, {
        onSuccess: (result) => onCreated?.(result.apiKey),
      });
    },
  });

  if (createOrganizationUserApiKeyMutation.data?.secretKey) {
    return (
      <>
        <DialogBar slot={DIALOG_SLOTS.TOP_BAR}>
          <Heading>Save your API key ID and secret key</Heading>
          <IconButton slot={DIALOG_SLOTS.CLOSE} />
        </DialogBar>

        <Form style={formStyles.wide}>
          {(renderProps) => (
            <>
              <Snackbar style={renderProps.fullWidthStyle} variant="warning">
                <Icon />
                <Text>The secret key won't be shown again — copy both values now</Text>
              </Snackbar>

              <TextField isReadOnly size={48} style={renderProps.fieldStyles} value={createOrganizationUserApiKeyMutation.data.apiKey}>
                <Label color={renderProps.labelColor}>API key ID</Label>
                <Input style={{
                  input: (inputRenderProps) => [
                    inputStyles.base,
                    inputRenderProps.isHovered && inputStyles.hovered,
                    inputRenderProps.isFocused && inputStyles.focused,
                  ],
                }}>
                  {(inputRenderProps) => (
                    <>
                      {inputRenderProps.defaultChildren}
                      <TooltipTrigger>
                        <IconButton
                          aria-label="Copy API key ID"
                          onPress={() => {
                            copyToClipboard(createOrganizationUserApiKeyMutation.data.apiKey);
                            setCopied([ ...copied, 'apiKey' ]);
                          }}>
                          {copied.includes('apiKey') ? <CheckmarkStrokeIcon /> : <CopyStrokeIcon />}
                        </IconButton>
                        <Tooltip>{copied.includes('apiKey') ? 'Copied' : 'Copy'}</Tooltip>
                      </TooltipTrigger>
                    </>
                  )}
                </Input>
              </TextField>

              <TextField isReadOnly size={48} style={renderProps.fieldStyles} value={createOrganizationUserApiKeyMutation.data.secretKey}>
                <Label color={renderProps.labelColor}>Secret key</Label>
                <Input style={{
                  input: (inputRenderProps) => [
                    inputStyles.base,
                    inputRenderProps.isHovered && inputStyles.hovered,
                    inputRenderProps.isFocused && inputStyles.focused,
                  ],
                }}>
                  {(inputRenderProps) => (
                    <>
                      {inputRenderProps.defaultChildren}
                      <TooltipTrigger>
                        <IconButton
                          aria-label="Copy secret key"
                          onPress={() => {
                            copyToClipboard(createOrganizationUserApiKeyMutation.data.secretKey!);
                            setCopied([ ...copied, 'secretKey' ]);
                          }}>
                          {copied.includes('secretKey') ? <CheckmarkStrokeIcon /> : <CopyStrokeIcon />}
                        </IconButton>
                        <Tooltip>{copied.includes('secretKey') ? 'Copied' : 'Copy'}</Tooltip>
                      </TooltipTrigger>
                    </>
                  )}
                </Input>
              </TextField>
            </>
          )}
        </Form>

        <DialogBar slot={DIALOG_SLOTS.BOTTOM_BAR}>
          <Button
            isDisabled={copied.length !== 2}
            isRounded
            size={40}
            variant="primary"
            onPress={onClose}>
            Close
          </Button>
        </DialogBar>
      </>
    );
  }

  return (
    <>
      <DialogBar slot={DIALOG_SLOTS.TOP_BAR}>
        <Heading>Add API key</Heading>
        <IconButton slot={DIALOG_SLOTS.CLOSE} />
      </DialogBar>

      <Form
        style={formStyles.base}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}>
        {(renderProps) => (
          <form.Field name="name">
            {(field) => (
              <TextField
                autoComplete="off"
                autoFocus
                id={field.name}
                name={field.name}
                size={48}
                style={renderProps.fieldStyles}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={field.handleChange}>
                <Label color={renderProps.labelColor}>Name</Label>
                <Input placeholder="e.g. Campaigns integration" />
                {field.state.meta.errors.length > 0 && (
                  <Hint style={renderProps.underFieldStyle}>
                    <Icon />
                    {String(field.state.meta.errors.at(0))}
                  </Hint>
                )}
              </TextField>
            )}
          </form.Field>
        )}
      </Form>

      <DialogBar slot={DIALOG_SLOTS.BOTTOM_BAR}>
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => <Button isPending={isSubmitting} isRounded size={40} onPress={form.handleSubmit}>Add</Button>}
        </form.Subscribe>
      </DialogBar>
    </>
  );
}

const formStyles = stylex.create({
  base: {
    width: {
      default: 480,
      [breakpoints.small]: '100%',
    },
  },
  wide: {
    width: {
      default: 540,
      [breakpoints.small]: '100%',
    },
  },
});

const inputStyles = stylex.create({
  base: { color: $.textPlaceholder },
  hovered: { color: $.textQuaternary },
  focused: { color: $.textQuaternary },
});
